import { layoutsSelectors } from 'store/feature/layoutsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { toggleLayoutGroupPin } from 'store/feature/layoutsSlice';
import LayoutIcons from '../../common/LayoutIcons';
import React, { useState } from 'react';
import LayoutManagerPanel from 'components/modal/layouts/manager/LayoutManagerPanel';
import { SavedLayoutModel } from 'store/feature/layoutsSlice/models';
import LayoutControls from 'components/common/LayoutControls';
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    ListItemIcon,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Settings from '@mui/icons-material/Settings';
import Star from '@mui/icons-material/Star';
import StarOutline from '@mui/icons-material/StarOutline';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import {
    bindMenu,
    bindTrigger,
    usePopupState,
} from 'material-ui-popup-state/hooks';
import { userSelectors } from 'store/feature/userSlice';
import TinyExpandableArea from 'components/common/TinyExpandableArea';
import useSwitchLayout from 'components/modal/layouts/dialogs/switchLayout';
import { StringFormatter } from 'utils';
import LightTooltip from 'components/common/LightTooltip';
import classNames from 'classnames';
import { LayoutsButtonStack, StyledLayoutDropdownMenu } from './styles';
import useLayoutMenuState from './useLayoutMenuState';
import useGridContext from 'hooks/grid/useGridContext';

const UserLayoutMenuItem: React.FC<{
    model: Pick<SavedLayoutModel, 'id' | 'name' | 'isTemplate' | 'isPreferred'>;
    isSelected: boolean;
    hasPendingChanges: boolean;
    isPinnedToCurrentGroup: boolean;
    onPinToggle: () => void;
    onClick: () => void;
    onDialogConfirmClick: () => void;
}> = ({
    model,
    isSelected,
    hasPendingChanges,
    isPinnedToCurrentGroup,
    onPinToggle,
    onClick,
    onDialogConfirmClick,
}) => {
    const icon = model.isPreferred ? <Star /> : <StarOutline />;
    const handleClick = () => {
        if (!isSelected) {
            onClick();
        }
    };

    const isTextLong = model.name.length > 35;
    const formattedText = StringFormatter.trimTitle(model.name, 35);

    const pinTooltip = isPinnedToCurrentGroup
        ? 'Remove as default for this group'
        : 'Set as default layout for this group';

    return (
        <MenuItem
            dense
            onClick={handleClick}
            disableRipple
            selected={isSelected}
            data-cy="LayoutMenuItem_User"
        >
            <ListItemIcon>{icon}</ListItemIcon>
            <Tooltip title={pinTooltip} placement="top" arrow>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPinToggle();
                    }}
                    sx={{ mr: 0.5, p: 0.25 }}
                >
                    {isPinnedToCurrentGroup ? (
                        <PushPin fontSize="small" />
                    ) : (
                        <PushPinOutlined fontSize="small" />
                    )}
                </IconButton>
            </Tooltip>

            <LightTooltip
                title={model.name}
                disableHoverListener={!isTextLong}
                disableInteractive
            >
                <Box className="item-text">
                    <Typography variant="h5">
                        {hasPendingChanges && isSelected && '*'}
                        {formattedText}
                    </Typography>
                </Box>
            </LightTooltip>
            {isSelected && hasPendingChanges && (
                <LayoutControls
                    model={model}
                    onDialogConfirmClick={onDialogConfirmClick}
                />
            )}
        </MenuItem>
    );
};

const TemplateLayoutMenuItem: React.FC<{
    model: Pick<SavedLayoutModel, 'id' | 'name' | 'isTemplate'>;
    isSelected: boolean;
    hasPendingChanges: boolean;
    onClick: () => void;
    onDialogConfirmClick: () => void;
}> = ({
    model,
    isSelected,
    hasPendingChanges,
    onClick,
    onDialogConfirmClick,
}) => {
    const handleClick = () => {
        if (!isSelected) {
            onClick();
        }
    };

    const isTextLong = model.name.length > 35;
    const formattedText = StringFormatter.trimTitle(model.name, 35);

    return (
        <MenuItem
            dense
            onClick={handleClick}
            disableRipple
            selected={isSelected}
            data-cy="LayoutMenuItem_Template"
        >
            <ListItemIcon>
                <LayoutIcons.Template />
            </ListItemIcon>

            <LightTooltip
                title={model.name}
                disableHoverListener={!isTextLong}
                disableInteractive
            >
                <Box className="item-text">
                    <Typography variant="h5">
                        {hasPendingChanges && isSelected && '*'}
                        {formattedText}
                    </Typography>
                </Box>
            </LightTooltip>
            {isSelected && hasPendingChanges && (
                <LayoutControls
                    model={model}
                    onDialogConfirmClick={onDialogConfirmClick}
                />
            )}
        </MenuItem>
    );
};

const TemplateLayoutMenuButton: React.FC<{
    model: Pick<SavedLayoutModel, 'id' | 'name' | 'isTemplate'>;
    isSelected: boolean;
    onClick: () => void;
}> = ({ model, isSelected, onClick }) => {
    const handleClick = () => {
        if (!isSelected) {
            onClick();
        }
    };

    return (
        <Badge
            className={classNames({ 'Fort-selected': isSelected })}
            badgeContent={isSelected && <CheckCircle />}
        >
            <Button
                variant="outlined"
                color="inherit"
                aria-selected={isSelected}
                disableRipple
                onClick={handleClick}
            >
                {model.name}
            </Button>
        </Badge>
    );
};

export interface LayoutsDropdownMenuProps {}

const LayoutsDropdownMenu: React.FC<LayoutsDropdownMenuProps> = () => {
    const { gridType } = useGridContext();
    const dispatch = useAppDispatch();
    const isAdmin = useAppSelector(userSelectors.isAdmin);
    const selectedLayout = useAppSelector(layoutsSelectors.selectedLayout);
    const hasPendingChanges = useAppSelector(
        layoutsSelectors.hasPendingChanges
    );
    const layouts = useAppSelector(layoutsSelectors.selectedDatasetLayouts);
    const currentGroup = useAppSelector(userSelectors.userSelectedGroup);
    const currentGroupId = currentGroup?.id;

    const userLayouts: SavedLayoutModel[] = [];
    const templateLayouts: SavedLayoutModel[] = [];
    layouts.forEach((l) => {
        if (l.isTemplate) {
            templateLayouts.push(l);
        } else {
            userLayouts.push(l);
        }
    });

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'layout_dropdown_menu',
    });
    const [showLayoutManager, setShowLayoutManager] = useState(false);

    const { switchLayout, switchLayoutDialog } = useSwitchLayout();

    const triggerText =
        (hasPendingChanges ? '*' : '') +
        StringFormatter.trimTitle(selectedLayout.name);

    const menuTitle = gridType + ' layouts';
    const divider = <Divider flexItem />;

    const { state: menuState, toggle: menuToggle } = useLayoutMenuState();

    return (
        <>
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: theme.spacing(3, 3),
                    '& .MuiButton-root': {},
                    '& .expand-icon': {
                        ...(popupState.isOpen && {
                            transform: 'rotate(180deg)',
                        }),
                    },
                })}
            >
                <Button
                    id="LayoutDropdownMenu_Trigger"
                    {...bindTrigger(popupState)}
                    disableElevation
                    disableRipple
                    variant="outlined"
                    color="info"
                    endIcon={<ArrowDropDown className="expand-icon" />}
                >
                    {triggerText}
                </Button>
            </Box>
            <StyledLayoutDropdownMenu
                {...bindMenu(popupState)}
                MenuListProps={{
                    'aria-labelledby': popupState.popupId,
                    dense: true,
                }}
                slotProps={{ paper: { id: 'LayoutDropdownMenu_Body' } }}
            >
                <Box className="menu-title">
                    {
                        <Typography variant="h4" color="primary">
                            {menuTitle}
                        </Typography>
                    }
                </Box>
                <TinyExpandableArea
                    idx="user"
                    title={
                        <Typography className="menu-subtitle" variant="h6">
                            My layouts
                        </Typography>
                    }
                    expanded={menuState.userSectionExpanded}
                    onChange={menuToggle.userSectionExpanded}
                >
                    {selectedLayout.isTemplate &&
                        !isAdmin &&
                        selectedLayout.selectedLayoutId && (
                            <TemplateLayoutMenuItem
                                model={{
                                    id: selectedLayout.selectedLayoutId,
                                    name: selectedLayout.name,
                                    isTemplate: true,
                                }}
                                isSelected={true}
                                hasPendingChanges={hasPendingChanges}
                                onClick={() => {
                                    switchLayout(
                                        selectedLayout.selectedLayoutId!
                                    );
                                    popupState.close();
                                }}
                                onDialogConfirmClick={popupState.close}
                            />
                        )}
                    {userLayouts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((layout) => (
                            <UserLayoutMenuItem
                                model={layout}
                                isSelected={
                                    layout.id ===
                                    selectedLayout.selectedLayoutId
                                }
                                hasPendingChanges={hasPendingChanges}
                                isPinnedToCurrentGroup={
                                    !!currentGroupId &&
                                    (layout.preferredForGroupIds ?? []).indexOf(
                                        currentGroupId
                                    ) !== -1
                                }
                                onPinToggle={() => {
                                    if (currentGroupId) {
                                        dispatch(
                                            toggleLayoutGroupPin({
                                                layoutId: layout.id,
                                                groupId: currentGroupId,
                                                gridType,
                                            })
                                        );
                                    }
                                }}
                                onClick={() => {
                                    switchLayout(layout.id);
                                    popupState.close();
                                }}
                                onDialogConfirmClick={popupState.close}
                            />
                        ))}
                </TinyExpandableArea>

                {templateLayouts.length > 0 && (
                    <>
                        {isAdmin && (
                            <>
                                {divider}
                                <TinyExpandableArea
                                    idx="templates-admin"
                                    title={
                                        <Typography
                                            className="menu-subtitle"
                                            variant="h6"
                                        >
                                            Layout templates (admin)
                                        </Typography>
                                    }
                                    expanded={
                                        menuState.adminTemplateSectionExpanded
                                    }
                                    onChange={
                                        menuToggle.adminTemplateSectionExpanded
                                    }
                                >
                                    {templateLayouts
                                        .sort((a, b) =>
                                            a.name.localeCompare(b.name)
                                        )
                                        .map((layout) => (
                                            <TemplateLayoutMenuItem
                                                model={layout}
                                                isSelected={
                                                    layout.id ===
                                                    selectedLayout.selectedLayoutId
                                                }
                                                hasPendingChanges={
                                                    hasPendingChanges
                                                }
                                                onClick={() => {
                                                    switchLayout(layout.id);
                                                    popupState.close();
                                                }}
                                                onDialogConfirmClick={
                                                    popupState.close
                                                }
                                            />
                                        ))}
                                </TinyExpandableArea>
                            </>
                        )}

                        {divider}
                        <TinyExpandableArea
                            idx="templates"
                            title={
                                <Typography
                                    className="menu-subtitle"
                                    variant="h6"
                                >
                                    Layout templates
                                </Typography>
                            }
                            expanded={menuState.templateSectionExpanded}
                            onChange={menuToggle.templateSectionExpanded}
                        >
                            <LayoutsButtonStack>
                                {templateLayouts
                                    .sort((a, b) =>
                                        a.name.localeCompare(b.name)
                                    )
                                    .map((layout) => (
                                        <TemplateLayoutMenuButton
                                            model={layout}
                                            isSelected={
                                                layout.id ===
                                                selectedLayout.selectedLayoutId
                                            }
                                            onClick={() => {
                                                switchLayout(layout.id);
                                                popupState.close();
                                            }}
                                        />
                                    ))}
                            </LayoutsButtonStack>
                        </TinyExpandableArea>
                    </>
                )}

                {divider}
                <MenuItem
                    dense
                    onClick={() => {
                        setShowLayoutManager(true);
                        popupState.close();
                    }}
                    disableRipple
                    data-cy="LayoutMenuItem_LayoutManager"
                >
                    <ListItemIcon>
                        <Settings />
                    </ListItemIcon>
                    <Typography
                        className="item-text"
                        variant="h5"
                        component="div"
                    >
                        Manage layouts
                    </Typography>
                </MenuItem>
            </StyledLayoutDropdownMenu>
            {switchLayoutDialog}
            <LayoutManagerPanel
                show={showLayoutManager}
                onClose={() => setShowLayoutManager(false)}
            />
        </>
    );
};

export default LayoutsDropdownMenu;
